import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CalendarDays, Clock, Video, ExternalLink, ShieldCheck, Waves } from 'lucide-react';
import { calendlyUrl, trialCalendlyUrl, issaBadgeHorizontal } from '../data/mock';

const OPTIONS = {
  intro: {
    id: 'intro',
    tab: 'Pre-Enrollment Call',
    eyebrow: 'Free service',
    title: 'Pre-Enrollment Virtual Overview',
    icon: Video,
    url: calendlyUrl,
    details: [
      { icon: Clock, text: '10 minute call' },
      { icon: Video, text: 'Zoom / Google Meet' },
      { icon: CalendarDays, text: 'Real-time availability via Calendly' },
    ],
    description:
      'Meet with our lead swim instructor Coach Abrie and go over key swim history to determine if Little Splash is a good fit for your family. No pressure — just a friendly conversation.',
  },
  assessment: {
    id: 'assessment',
    tab: 'Swim Assessment',
    eyebrow: 'In-water · $25',
    title: '20-Minute Swim Assessment · $25',
    icon: Waves,
    url: trialCalendlyUrl,
    details: [
      { icon: Clock, text: '20 minute in-pool session' },
      { icon: Waves, text: 'One-on-one with Coach Abrie' },
      { icon: CalendarDays, text: 'Real-time availability via Calendly' },
    ],
    description:
      'Bring your swimmer in for a 1:1 swim assessment ($25) so we can place them in the right program, and you can see how Coach Abrie teaches, feel the calm environment, and give your child a taste of the water before enrolling.',
  },
};

export default function Booking() {
  const { search } = useLocation();
  const initial = new URLSearchParams(search).get('type');
  const initialKey = ['assessment', 'trial'].includes(initial) ? 'assessment' : 'intro';
  const [active, setActive] = useState(initialKey);
  const option = OPTIONS[active];

  const Icon = option.icon;

  return (
    <main>
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">Book Online</div>
            <h1 className="font-display font-bold text-3xl md:text-5xl text-splash-green leading-tight">
              Choose your booking
            </h1>
            <p className="mt-3 text-splash-green/75 max-w-2xl mx-auto text-sm md:text-base">
              Start with a free virtual overview, or book a 20-minute in-pool swim assessment ($25) with Coach Abrie.
            </p>
          </div>

          {/* Tabs */}
          <div
            data-testid="booking-tabs"
            className="mx-auto mb-8 grid grid-cols-2 gap-1.5 bg-white border border-splash-sand rounded-2xl p-1.5 w-full max-w-md"
          >
            {Object.values(OPTIONS).map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setActive(o.id)}
                data-testid={`booking-tab-${o.id}`}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold btn-transition ${
                  active === o.id
                    ? 'bg-splash-coral text-white shadow-sm'
                    : 'bg-transparent text-splash-green hover:bg-splash-cream'
                }`}
              >
                {o.tab}
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-2 bg-white rounded-3xl border border-splash-sand p-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-splash-teal/15 text-splash-teal grid place-items-center">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-splash-green/60">{option.eyebrow}</div>
                  <div className="font-display font-bold text-xl text-splash-green">{option.title}</div>
                </div>
              </div>
              <div className="my-6 border-t border-splash-sand" />
              <ul className="space-y-3 text-splash-green/85">
                {option.details.map((d, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <d.icon className="w-4 h-4 text-splash-teal" /> {d.text}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-splash-green/75 leading-relaxed">{option.description}</p>
              <a
                href={option.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`booking-open-${option.id}`}
                className="inline-flex mt-6 items-center gap-2 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-5 py-2.5 font-semibold btn-transition"
              >
                Open in new tab <ExternalLink className="w-4 h-4" />
              </a>

              <div className="mt-8 rounded-2xl border border-splash-sand bg-splash-cream p-5 flex items-start gap-4">
                <ShieldCheck className="w-6 h-6 text-splash-teal shrink-0" />
                <div>
                  <div className="font-semibold text-splash-green">ISSA Accredited Swim School</div>
                  <div className="text-sm text-splash-green/70 mt-0.5">Nationally recognized standards of safety and instruction.</div>
              <img src={issaBadgeHorizontal} alt="ISSA Accredited Swim School" loading="lazy" decoding="async" className="mt-3 h-8 object-contain" />
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 bg-white rounded-3xl border border-splash-sand overflow-hidden">
              <iframe
                key={active}
                title={option.title}
                src={`${option.url}?hide_gdpr_banner=1&primary_color=e89b8b&embed_type=Inline`}
                width="100%"
                height="780"
                frameBorder="0"
                data-testid={`booking-widget-${option.id}`}
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
